import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Package,
  Warehouse as WarehouseIcon,
  FileText,
  RotateCcw,
} from "lucide-react";
import { getMyStockRequestById } from "../services/api";
import "../styles/SaleDetails.css";
import "../styles/DisplayProofDetails.css";
import "../components/SalesExecutive/StockRequest.css";

function StockRequestDetails() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { requestData, previousView } = location.state || {};

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const requestId = Number(requestData?.stock_request_id);
    if (!Number.isInteger(requestId) || requestId <= 0) {
      setError(
        t("se.stockRequest.details.noData", {
          defaultValue: "No stock request data found.",
        }),
      );
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      try {
        setLoading(true);
        const response = await getMyStockRequestById(requestId);
        if (response?.success && response?.data) {
          setRequest(response.data);
        } else {
          setError(
            t("se.stockRequest.details.errorFetch", {
              defaultValue: "Failed to fetch stock request details.",
            }),
          );
        }
      } catch (err) {
        setError(
          err?.response?.data?.error ||
            t("se.stockRequest.details.errorFetch", {
              defaultValue: "Failed to fetch stock request details.",
            }),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [requestData, t]);

  const goBack = () =>
    navigate("/promoter", {
      state: { view: previousView || "HISTORY" },
    });

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-MY", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="sale-details-container">
        <div className="sale-items-card">
          <div className="items-header">
            <Package size={20} />
            <h3>
              {t("se.stockRequest.details.loading", {
                defaultValue: "Loading stock request...",
              })}
            </h3>
          </div>
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="sale-details-container">
        <div className="error-state">
          <p>
            {error ||
              t("se.stockRequest.details.noData", {
                defaultValue: "No stock request data found.",
              })}
          </p>
          <button onClick={goBack} className="back-button">
            <ArrowLeft size={16} />
            {t("se.saleDetails.goBack")}
          </button>
        </div>
      </div>
    );
  }

  const items = Array.isArray(request.items) ? request.items : [];
  const totalUnits = items.reduce(
    (sum, item) => sum + (Number.parseInt(item.quantity, 10) || 0),
    0,
  );
  const orderLabel =
    request.order_no || `#${request.stock_request_id}`;

  return (
    <div className="sale-details-container">
      <div className="se-header">
        <div className="se-header-title">
          <button onClick={goBack} className="se-back-btn">
            <ArrowLeft size={24} />
          </button>
          <span className="display-proof-title">
            {t("se.stockRequest.details.title", {
              id: orderLabel,
              defaultValue: "Stock Request {{id}}",
            })}
          </span>
        </div>
        <span className={`sr-history-badge is-${request.status}`}>
          {t(`se.stockRequest.history.status.${request.status}`, {
            defaultValue: request.status,
          })}
        </span>
      </div>

      <div className="sale-details-content">
        <div className="sale-info-card">
          <div className="sale-info-header">
            <div className="sale-date">
              <Calendar size={16} />
              <span>{formatDate(request.requested_at)}</span>
            </div>
          </div>

          <div className="sale-info-body">
            <div className="sale-info-panel sale-info-panel-highlight">
              <div className="sale-info-panel-icon">
                <MapPin size={18} />
              </div>
              <div className="sale-info-panel-content">
                <div className="sale-info-panel-label">
                  {t("se.stockRequest.details.outletLabel", {
                    defaultValue: "Outlet",
                  })}
                </div>
                <div className="sale-info-panel-value">
                  {request.outlet?.bp_outlet_name ||
                    t("se.displayProof.unknownOutlet")}
                </div>
              </div>
            </div>

            {request.warehouse ? (
              <div className="sale-info-panel">
                <div className="sale-info-panel-icon">
                  <WarehouseIcon size={18} />
                </div>
                <div className="sale-info-panel-content">
                  <div className="sale-info-panel-label">
                    {t("se.stockRequest.details.warehouseLabel", {
                      defaultValue: "Warehouse",
                    })}
                  </div>
                  <div className="sale-info-panel-value sale-info-panel-value-muted">
                    {[
                      request.warehouse.warehouse_code,
                      request.warehouse.warehouse_name,
                    ]
                      .filter(Boolean)
                      .join(" ") || t("se.saleDetails.notProvided")}
                  </div>
                </div>
              </div>
            ) : null}

            {request.resubmittedFrom ? (
              <div className="sale-info-panel">
                <div className="sale-info-panel-icon">
                  <RotateCcw size={18} />
                </div>
                <div className="sale-info-panel-content">
                  <div className="sale-info-panel-label">
                    {t("se.stockRequest.details.resubmittedFromLabel", {
                      defaultValue: "Resubmitted from",
                    })}
                  </div>
                  <div className="sale-info-panel-value sale-info-panel-value-muted">
                    {request.resubmittedFrom.order_no ||
                      `#${request.resubmittedFrom.stock_request_id}`}
                  </div>
                </div>
              </div>
            ) : null}

            {request.reviewed_at ? (
              <div className="sale-info-panel">
                <div className="sale-info-panel-icon">
                  <Calendar size={18} />
                </div>
                <div className="sale-info-panel-content">
                  <div className="sale-info-panel-label">
                    {t("se.stockRequest.details.reviewedAtLabel", {
                      defaultValue: "Reviewed at",
                    })}
                  </div>
                  <div className="sale-info-panel-value sale-info-panel-value-muted">
                    {formatDate(request.reviewed_at)}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="sale-items-card">
          <div className="items-header">
            <Package size={20} />
            <h3>
              {t("se.stockRequest.details.itemsHeading", {
                defaultValue: "Requested Items",
              })}
            </h3>
          </div>

          {items.length > 0 ? (
            <ul className="sr-history-items sr-details-items">
              {items.map((item) => (
                <li key={item.stock_request_item_id}>
                  <span>
                    {item.model_name || item.product_name || "-"}
                    {item.product_code ? (
                      <span className="sr-details-item-code">
                        {" "}
                        ({item.product_code})
                      </span>
                    ) : null}
                  </span>
                  <strong>× {item.quantity}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-items">
              {t("se.stockRequest.details.noItems", {
                defaultValue: "No items on this request.",
              })}
            </p>
          )}

          <div className="sr-details-total">
            {t("se.stockRequest.history.productCount", {
              count: items.length,
            })}
            {" · "}
            {t("se.stockRequest.history.unitCount", { count: totalUnits })}
          </div>
        </div>

        {request.notes ? (
          <div className="sale-items-card">
            <div className="items-header">
              <FileText size={20} />
              <h3>
                {t("se.stockRequest.details.yourNote", {
                  defaultValue: "Your note",
                })}
              </h3>
            </div>
            <p className="sr-details-note">{request.notes}</p>
          </div>
        ) : null}

        {request.review_notes ? (
          <div className="sale-items-card">
            <div className="items-header">
              <FileText size={20} />
              <h3>
                {request.status === "rejected"
                  ? t("se.stockRequest.details.rejectionReason", {
                      defaultValue: "Reason for rejection",
                    })
                  : t("se.stockRequest.details.adminNote", {
                      defaultValue: "Admin note",
                    })}
              </h3>
            </div>
            <p
              className={`sr-details-note${
                request.status === "rejected" ? " is-rejection" : ""
              }`}
            >
              {request.review_notes}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default StockRequestDetails;
